import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import * as crypto from 'node:crypto';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Student } from '../student.js';
import { normalizeJoinCode } from '../utils/join-code.js';

export const requestToJoinClassMutation = registerMutation({
  operationName: 'requestToJoinClassMutation',
  type: () => ClassMembership,
  params: [{ name: 'code', type: () => String }],

  resolve: (code: string) => async (_gqlFields: BaseQLSelectionSet<ClassMembership>) => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!currentUser?._id) {
      throw new Error('Nicht autorisiert: Kein User eingeloggt');
    }

    const studentCollection = await getCollection(Student);
    const student = await studentCollection.findOne({ userID: currentUser._id });
    if (!student) {
      throw new Error('Nicht autorisiert: Kein Schülerprofil gefunden');
    }

    const normalizedCode = normalizeJoinCode(code);
    if (!normalizedCode) {
      throw new Error('Ungültiger Code');
    }

    const classroomCollection = await getCollection(ClassRoom);
    const classroom = await classroomCollection.findOne({ code: normalizedCode });
    if (!classroom) {
      throw new Error('Ungültiger Code');
    }

    const membershipCollection = await getCollection(ClassMembership);
    const existing = await membershipCollection.findOne({ classRoomID: classroom._id, studentID: student._id });

    if (existing) {
      if (existing.status === 'pending' || existing.status === 'approved') {
        return existing as ClassMembership;
      }

      // Previously rejected — a fresh request reopens it rather than inserting a second row.
      const requestedAt = new Date().toISOString();
      await membershipCollection.updateOne(
        { _id: existing._id },
        { $set: { status: 'pending', requestedAt }, $unset: { decidedAt: '' } },
      );
      return new ClassMembership({ ...existing, status: 'pending', requestedAt, decidedAt: undefined });
    }

    const membership = new ClassMembership({
      _id: crypto.randomUUID(),
      classRoomID: classroom._id,
      studentID: student._id,
      teacherID: classroom.teacherID,
      status: 'pending',
      requestedAt: new Date().toISOString(),
    });

    await membershipCollection.insertOne(membership.toObject());

    return membership;
  },
});
